import React, {useState} from "react";
import {Autocomplete, Backdrop, CircularProgress, FormLabel} from "@mui/material";
import {Controller, UseFormRegisterReturn} from "react-hook-form";
import {Close} from "@mui/icons-material";
import {UploadFileVO, useUploadFile} from "@/service/common.s";
import Box from "@mui/material/Box";
import isEmpty from "lodash/isEmpty";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import {Control} from "react-hook-form/dist/types/form";
import {TagOut} from "@/widget/TagOut/TagOut";
import {gray} from "@/theme/themePrimitives";
import * as z from "zod";
import {FilePO} from "@/model/file";


export const validationFileSchema = z.object({
    id: z.unknown(),
    name: z.string(),
    size: z.number().optional(),
    type: z.string().optional(),
    url: z.string().optional(),
    tag: z.unknown(),
    targetId: z.unknown(),
})

type FileModeType = "multiple" | "single"

interface UploadFileProps<T> {
    control: Control<any, Partial<T>>
    errMsg?: string
    label?: string
    tag?: number
    targetId?: number
    isSave?: boolean
    uploadCallback?: (file: UploadFileVO) => void
    mode: FileModeType
    maxTagWidth?: string
    attr: UseFormRegisterReturn<string>
}

export const InputFile = <T, >(props: UploadFileProps<T>) => {
    const {control, errMsg, attr, label, tag = 0, maxTagWidth, targetId = 0, uploadCallback, isSave = false, mode} = props;
    const {mutateAsync: uploadFileMutate} = useUploadFile()
    const [uploading, setUploading] = useState<boolean>(false);

    const onChangeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files

        if (files!.length > 0) {
            const file = files![0]
            const formData = new FormData();
            formData.append("file", file)
            formData.append("isSave", isSave?.toString())
            formData.append("targetId", targetId?.toString())
            formData.append("tag", tag?.toString())
            setUploading(true);
            const result = await uploadFileMutate({
                formData, onUploadProgress: (event) => {
                    setUploading(event.progress != 1)
                }
            })
            setUploading(false)
            return result.data

        }
    }

    return <FormControl fullWidth={true} required={false}>
        {label && <FormLabel htmlFor="originals">{label}</FormLabel>}
        <Box component={'div'} sx={{
            position: "relative",
            width: 'auto',
            minHeight: "40px",
            zIndex: 0,
        }}>
            <Backdrop sx={{position: 'absolute', zIndex: 999, borderRadius: 2, backgroundColor: 'transparent'}} open={uploading}>
                <CircularProgress size={18} color="inherit"/>
            </Backdrop>
            <Controller control={control} render={({field: {onChange, value}}) => {
                const content = isEmpty(value) ? '"点击上传"' : '""'
                return <Autocomplete
                    id={attr.name}
                    multiple={true}
                    freeSolo={true}
                    options={[]}
                    value={value}
                    readOnly={uploading}
                    popupIcon={<Close/>}
                    disableListWrap={true}
                    disableCloseOnSelect={true}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            paddingRight: '6px !important',
                            "&::before": {
                                content: content,
                                opacity: 0.7,
                                color: gray[500],
                                display: 'inline-block',
                            }
                        },
                        "& .MuiAutocomplete-endAdornment": {
                            display: 'none',
                        }
                    }}
                    renderTags={(values: UploadFileVO[]) => {
                        const tags: React.ReactNode[] = []
                        let empty: boolean = false
                        const emptyNode = <Box color={"#8791a6"} key={-100} component={'span'}>上传文件</Box>
                        const newValues = Array.from(values)
                        newValues.forEach((value: UploadFileVO, index: number) => {
                            if (isEmpty(value)) {
                                empty = true
                                tags.push(emptyNode)
                            } else {
                                const rate = value.rate || 1
                                tags.push(<TagOut maxWidth={maxTagWidth} data-tag-index={value.id!} index={-1} onDelete={() => {
                                    newValues.splice(index, 1)
                                    onChange(newValues)
                                }} key={index} label={`${rate < 1 ? `上传中${Math.ceil(rate) * 100}%-` : ''}${value.name}`}/>)
                            }
                        })
                        if (!empty) {
                            tags.push(emptyNode)
                        }
                        return tags
                    }}
                    clearIcon={false}
                    renderInput={(params) => (
                        <FormLabel required={false}>
                            <TextField
                                error={!!errMsg} type={'file'}
                                sx={{
                                    // "& .MuiInputBase-input": {opacity: 0},
                                    'input[type="file"]': {display: 'none'},
                                    "& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}
                                }}
                                slotProps={{inputLabel: {shrink: true}}}
                                onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                                    const result = await onChangeFile(event)
                                    if (result) {
                                        if (mode === "multiple") {
                                            value.push(result)
                                            onChange(value)
                                        } else {
                                            onChange([result])
                                        }
                                        if (uploadCallback) {
                                            uploadCallback(result)
                                        }
                                    }
                                }}
                                helperText={errMsg}  {...params} size={'small'} id={attr.name}>
                            </TextField>
                        </FormLabel>
                    )}
                />
            }} name={attr.name}/>
        </Box>
    </FormControl>
}